import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import {
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Input,
  InputGroup,
  Loader,
  Pagination,
  Table,
} from "rsuite";
import { OrderServices } from "../../../core/services/OrderServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { OrderFields, OrderListParams, Permission } from "./OrderDataTypes";
import PhoneCell from "../../components/Elements/PhoneCell";
import "./Orders.css";
import {
  displayDateField,
  formatDateOnly,
  isEmptyVal,
  isNotEmpty,
  processMinute,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import { ToastContainer } from "react-toastify";
import { displayField } from "../../../utils/helpers";
import { appConstObj } from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import CheckCell from "../../components/Elements/CheckCell";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import CustomerCell from "../../components/Elements/CustomerCell";
import OrderItemsCell from "../../components/Elements/OrderItemsCell";
import OrderAmountCell from "../../components/Elements/OrderAmountCell";
import OrderAssignedServedCell from "../../components/Elements/OrderAssignedServedCell";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function ViewOrders({
  orderFilterData,
  withHeader = true,
  withSmartFilter = true,
  ...props
}: {
  orderFilterData?: any;
  withHeader?: boolean;
  withSmartFilter?: boolean;
}) {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<OrderFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);
  const [selectedOrder, setSelectedOrderData] = useState<any>({});
  const modalRef = useRef<ImageModal>(null);

  const initSearchFormData = { ...orderFilterData };
  const [formData, setFormData] = useState<OrderListParams>(initSearchFormData);

  if (checkedKeys.length === data.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (checkedKeys.length > 0 && checkedKeys.length < data.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function customerPicModal(value: any) {
    if (
      !failedImages.includes(value.customer_large_image_url) &&
      value.customer_large_image_url != null &&
      value.customer_large_image_url !== ""
    ) {
      setSelectedOrderData(value);
      modalRef.current?.openModal();
      modalRef.current?.hideDelete();
    }
  }

  const filterOrders = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getOrders(
        getSearchParams({
          smart_search: smartSearchInput,
          rows: rows,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getOrders(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  async function getOrders(params: OrderListParams) {
    try {
      setLoading(true);
      const payload = {
        ...params,
        service_date: params.service_date
          ? formatDateOnly(params.service_date)
          : null,
      };
      const res = await OrderServices.getOrders(payload);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
    getOrders(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, []);

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };

    let params: OrderListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }

    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getOrders(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getOrders(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getOrders(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  return (
    <div className="products">
      <Container className="container">
        {withHeader && (
          <Header className="header">
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item colspan={8}>
                <h4>{t("ORDERS")}</h4>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Header>
        )}

        <Content>
          <div className="row">
            {withSmartFilter && (
              <div className="col-md-6">
                <InputGroup>
                  <Input
                    className="form-control"
                    placeholder={t("SMART_SEARCH_BY", {
                      entity:
                        t("CUSTOMER").toLowerCase() +
                        " " +
                        t("NAME").toLowerCase() +
                        ", " +
                        t("PHONE").toLowerCase() +
                        ", " +
                        t("OR").toLowerCase() +
                        " " +
                        t("ORDER_NO").toLowerCase(),
                    })}
                    value={smartSearchInput}
                    onChange={(val: any) => setSmartSearch(val)}
                    onKeyDown={(e: any) => filterOrders(e)}
                  />
                  <InputGroup.Button
                    title={t("SEARCH")}
                    onClick={(e: any) => filterOrders(e)}
                  >
                    <SearchIcon />
                  </InputGroup.Button>
                  <InputGroup.Button
                    title={t("CLEAR")}
                    onClick={() => clearSmartSearch()}
                  >
                    <CloseIcon />
                  </InputGroup.Button>
                </InputGroup>
              </div>
            )}
          </div>

          <div
            className={
              total > 0 ? "order-finder-table mt-2" : "no-record-table mt-2"
            }
          >
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              {!orderFilterData.customer && (
                <>
                  <Column width={30} align="center">
                    <CustomHeaderCell>
                      <div>
                        <Checkbox
                          inline
                          checked={checked}
                          indeterminate={indeterminate}
                          onChange={handleCheckAll}
                        />
                      </div>
                    </CustomHeaderCell>
                    <CheckCell
                      dataKey="id"
                      checkedKeys={checkedKeys}
                      onChange={handleCheck}
                    />
                  </Column>
                  <Column width={50} align="center">
                    <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
                    <ImageCellFormatter
                      dataKey="customer_large_image_url"
                      rowData={(rowData: any) => rowData}
                      dummyImage={UtilityServices.getLoggedInUser(
                        "user_default_image"
                      )}
                      onFailedImages={getFailedImages}
                      openPictureModal={customerPicModal}
                    />
                  </Column>
                  <Column width={130} sortable resizable>
                    <CustomHeaderCell>{t("CUSTOMER_NAME")}</CustomHeaderCell>
                    <CustomerCell
                      rowData={(rowData: any) => rowData}
                      dataKey="customer_name"
                    />
                  </Column>
                  <Column width={100} resizable>
                    <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
                    <PhoneCell
                      positionFromRight="60%"
                      phoneIndex="customer_phone"
                      rowData={(rowData: any) => rowData}
                    />
                  </Column>
                </>
              )}

              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("DATE")}</CustomHeaderCell>
                <CustomCell dataKey="service_date">
                  {(rowData: any) => (
                    <span>{displayDateField(rowData.service_date)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={80} sortable>
                <CustomHeaderCell>{t("TIME")}</CustomHeaderCell>
                <CustomCell dataKey="service_starts_at" />
              </Column>
              <Column width={200} flexGrow={1}>
                <CustomHeaderCell>{t("ITEMS")}</CustomHeaderCell>
                <OrderItemsCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={80} resizable>
                <CustomHeaderCell>{t("AMOUNT")}</CustomHeaderCell>
                <OrderAmountCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={80}>
                <CustomHeaderCell>{t("ORDER_STATUS")}</CustomHeaderCell>
                <OrderAssignedServedCell
                  displayTitle="order_status"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column width={140} resizable>
                <CustomHeaderCell>{t("NOTE")}</CustomHeaderCell>
                <CustomCell dataKey="customer_order_note">
                  {(rowData: any) => (
                    <span title={displayField(rowData.customer_order_note)}>
                      {displayField(rowData.customer_order_note)}
                    </span>
                  )}
                </CustomCell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
    </div>
  );
}

export default ViewOrders;
